import { LinearProgress } from "@mui/material";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { formatAppointmentRequestAvailableDaysText } from "@/helper/appointmentRequestAvailableDays";
import { AppointmentRequestNoAvailableDaysMessage } from "@/components/appointmentRequest/appointmentRequestNoAvailableDaysMessage";
import {
  AppointmentListRow,
  AppointmentLocationLine,
} from "@/components/appointmentRequest/appointmentListItemParts";
import styles from "@/components/appointmentRequest/appointmentListItem.module.css";

interface Props {
  item: any;
}

const acceptedPercentage = (item: any) => {
  const eligibleCount = item.eligible_worker_count ?? item.invited_user_count;
  const bookedCount = item.booked_worker_count ?? item.accepted_user_count;
  if (!eligibleCount) return 0;
  return Math.round((100 / eligibleCount) * bookedCount);
};

export default function ManagerAppointmentRequestListItem(props: Props) {
  const { item } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const availableDaysText = formatAppointmentRequestAvailableDaysText(item);
  const hasAvailableSlots = Boolean(item.available_day_set?.length);
  const hasBookableWorkers = item.bookable_user_count > 0;
  const canBook = hasAvailableSlots && hasBookableWorkers;
  const eligibleCount = item.eligible_worker_count ?? item.invited_user_count;
  const bookedCount = item.booked_worker_count ?? item.accepted_user_count;
  const assignmentLabel = !hasBookableWorkers
    ? eligibleCount > 0 && bookedCount >= eligibleCount
      ? "all_workers_assigned"
      : "no_more_worker_assignments"
    : !hasAvailableSlots
    ? "no_more_appointment_slots"
    : "assign_worker_appointment";

  return (
    <AppointmentListRow>
      <div className={styles.managerRequestHeader}>
        <h4 className={styles.title}>{item.title}</h4>
      </div>
      <div className={styles.managerRequestDetails}>
        <span className={styles.dateLine}>
          {availableDaysText ?? <AppointmentRequestNoAvailableDaysMessage />}
        </span>
        {item.department_set?.length > 0 && (
          <div className={styles.chipList}>
            {item.department_set.map((department: any) => (
              <span
                key={department.id ?? department.name}
                className={styles.chip}
              >
                {department.name}
              </span>
            ))}
          </div>
        )}
        <div className={styles.managerRequestActions}>
          <GenericButton
            data-testid={`manager-appointment-request-choose-${item.id}`}
            startIcon={<PersonAddAltOutlinedIcon />}
            title={
              !hasAvailableSlots
                ? t("error_all_slots_taken_please_contact")
                : !hasBookableWorkers
                ? t("no_workers_available_for_booking")
                : undefined
            }
            disabled={!canBook}
            onClick={() =>
              navigate(
                `/appointmentrequestopen/${item.id}/pick?booking=workers`,
              )
            }
          >
            {t(assignmentLabel)}
          </GenericButton>
          {!canBook && bookedCount > 0 && (
            <GenericButton
              variant="outlined"
              onClick={() =>
                navigate(
                  `/appointmentrequestopen/${item.id}/pick?booking=workers`,
                )
              }
            >
              {t("planner.manage_assignments")}
            </GenericButton>
          )}
        </div>
      </div>
      <AppointmentLocationLine item={item} />
      <div className={styles.progress}>
        <LinearProgress
          variant="determinate"
          value={acceptedPercentage(item)}
          className={styles.progressBar}
          classes={{ bar: styles.progressBarFill }}
        />
        <span className={styles.progressCaption}>
          <span>{t("registered_appointmentrequests")}</span>
          <span className={styles.progressValue}>
            {item.booked_worker_count ?? item.accepted_user_count} /{" "}
            {item.eligible_worker_count ?? item.invited_user_count}
          </span>
        </span>
      </div>
    </AppointmentListRow>
  );
}
